/**
 * Novera Pro — decimal parsing and display (ux/01 §3.2, OP-F-04, AUD-UXDS-12).
 *
 * One place that turns a decimal STRING (what the server sends for Prisma
 * `Decimal` columns, and what the typed inputs emit) into the programme-wide
 * display format, and a typed entry back into a canonical decimal string.
 * Nothing here does money arithmetic on binary floats: values are scaled to
 * BigInt when they have to be compared or rounded.
 *
 * Display format (ux/01 §3.2 « Formats »):
 *   · thousands separator  U+202F narrow no-break space  → « 1 250,00 »
 *   · decimal separator    comma
 *   · negative sign        U+2212 true minus, never parentheses → « −3 200,00 »
 *   · unknown / not applicable → « — »
 */

/** U+202F — the thousands separator of every displayed number. */
export const THOUSANDS_SEP = "\u202F";
/** U+2212 — the true minus sign of every displayed negative number. */
export const MINUS_SIGN = "\u2212";
/** What the UI shows for an unknown or not-applicable value. */
export const UNKNOWN_VALUE = "—";

// Spaces a user (or a paste from a displayed value) may leave inside a number.
const SPACES = /[\s\u00A0\u202F]/g;
const CANONICAL = /^-?\d+(\.\d+)?$/;

/**
 * Normalise anything numeric-looking into a canonical decimal string
 * (`"-1250.5"`), or null when it is not a finite number. Numbers go through
 * Intl (exact decimal expansion of the double, no exponent); strings are kept
 * digit for digit.
 * @param {unknown} v
 * @returns {string|null}
 */
export function toDecimalString(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    return new Intl.NumberFormat("en-US", {
      useGrouping: false,
      maximumFractionDigits: 20,
    }).format(v).replace(MINUS_SIGN, "-");
  }
  if (typeof v === "bigint") return v.toString();
  // Prisma Decimal / decimal.js instances serialise through toString().
  const s = String(typeof v === "object" && "toString" in v ? v.toString() : v)
    .trim()
    .replace(SPACES, "")
    .replace(MINUS_SIGN, "-")
    .replace(/^\+/, "");
  if (!CANONICAL.test(s)) {
    // Exponent notation from a Decimal (1e-7) — fall back to Number once.
    const n = Number(s);
    return /e/i.test(s) && Number.isFinite(n) ? toDecimalString(n) : null;
  }
  return s;
}

/** Split a canonical decimal string into sign, integer and fraction digits. */
function split(dec) {
  const neg = dec.startsWith("-");
  const body = neg ? dec.slice(1) : dec;
  const [int, frac = ""] = body.split(".");
  return { neg, int: int.replace(/^0+(?=\d)/, ""), frac };
}

/**
 * Round a canonical decimal string half away from zero to `decimals` places.
 * @param {string} dec
 * @param {number} decimals
 * @returns {string} canonical, with exactly `decimals` fraction digits
 */
export function roundDecimal(dec, decimals) {
  const { neg, int, frac } = split(dec);
  const padded = (frac + "0".repeat(decimals + 1)).slice(0, decimals + 1);
  let scaled = BigInt(int + padded.slice(0, decimals));
  if (Number(padded[decimals]) >= 5) scaled += 1n;
  const digits = scaled.toString().padStart(decimals + 1, "0");
  const i = digits.slice(0, digits.length - decimals) || "0";
  const f = decimals > 0 ? digits.slice(digits.length - decimals) : "";
  const isZero = /^0*$/.test(i + f);
  return `${neg && !isZero ? "-" : ""}${i}${f ? "." + f : ""}`;
}

/**
 * Compare two canonical decimal strings exactly. Returns -1, 0 or 1.
 * @param {string} a
 * @param {string} b
 */
export function compareDecimal(a, b) {
  const A = split(a);
  const B = split(b);
  const scale = Math.max(A.frac.length, B.frac.length);
  const toBig = (x) =>
    BigInt((x.neg ? "-" : "") + x.int + x.frac.padEnd(scale, "0"));
  const d = toBig(A) - toBig(B);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

/**
 * Add two canonical decimal strings exactly (grid totals of money / quantity
 * columns — never a float sum).
 * @param {string} a
 * @param {string} b
 * @returns {string} canonical, with the larger scale of the two
 */
export function addDecimal(a, b) {
  const A = split(a);
  const B = split(b);
  const scale = Math.max(A.frac.length, B.frac.length);
  const toBig = (x) =>
    BigInt((x.neg ? "-" : "") + x.int + x.frac.padEnd(scale, "0"));
  const sum = toBig(A) + toBig(B);
  const neg = sum < 0n;
  const digits = (neg ? -sum : sum).toString().padStart(scale + 1, "0");
  const i = digits.slice(0, digits.length - scale);
  const f = scale > 0 ? digits.slice(digits.length - scale) : "";
  return `${neg ? "-" : ""}${i}${f ? "." + f : ""}`;
}

/**
 * Display a number in the programme format.
 * @param {unknown} v              number, decimal string or Decimal
 * @param {object}  [opts]
 * @param {number}  [opts.decimals=2]
 * @param {boolean} [opts.grouping=true]  thousands separators
 * @param {boolean} [opts.signed=false]   « + » on positive values (écarts, soldes)
 * @returns {string} « 1 250,00 », « −3 200,00 », « +73,42 » or « — »
 */
export function formatDecimal(v, { decimals = 2, grouping = true, signed = false } = {}) {
  const dec = toDecimalString(v);
  if (dec == null) return UNKNOWN_VALUE;
  const rounded = roundDecimal(dec, decimals);
  const { neg, int, frac } = split(rounded);
  const grouped = grouping
    ? int.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEP)
    : int;
  const isZero = /^0*$/.test(int + frac);
  const sign = neg ? MINUS_SIGN : signed && !isZero ? "+" : "";
  return `${sign}${grouped}${frac ? "," + frac : ""}`;
}

/**
 * The text a numeric field shows while it is being edited: comma decimal,
 * no thousands separators, ASCII minus (ux/01 §3.2 — separators are never
 * inserted while the user types).
 * @param {string|null|undefined} value canonical decimal string
 */
export function toEditText(value) {
  const dec = toDecimalString(value);
  return dec == null ? "" : dec.replace(".", ",");
}

/**
 * Parse what the user typed into a numeric field.
 *
 * `,` and `.` are both accepted as the decimal separator; spaces (including the
 * display separators) are ignored; a leading « − », « - » or « + » is a sign.
 * A value that does not parse is NEVER coerced: the caller keeps the text and
 * shows the error.
 *
 * @param {string} text
 * @param {object} [opts]
 * @param {number} [opts.decimals=2]       0 = integers only
 * @param {string|number} [opts.min]
 * @param {string|number} [opts.max]
 * @param {boolean} [opts.allowNegative=true]
 * @returns {{ value: string|null, error: string|null }}
 *   value = canonical decimal string padded to `decimals` (« 1250.50 »),
 *   null when the field is empty or invalid.
 */
export function parseDecimalInput(
  text,
  { decimals = 2, min, max, allowNegative = true } = {},
) {
  const raw = String(text ?? "").replace(SPACES, "").replace(MINUS_SIGN, "-");
  if (raw === "") return { value: null, error: null };
  const m = /^([+-]?)(\d*)(?:[.,](\d*))?$/.exec(raw);
  if (!m || (m[2] === "" && !m[3])) {
    return { value: null, error: "Saisissez un nombre" };
  }
  const [, sign, int, frac = ""] = m;
  if (sign === "-" && !allowNegative) {
    return { value: null, error: "Valeur négative non autorisée" };
  }
  if (frac.length > decimals) {
    return {
      value: null,
      error:
        decimals === 0
          ? "Saisissez un nombre entier"
          : `${decimals} décimale${decimals > 1 ? "s" : ""} au plus`,
    };
  }
  const canonical = roundDecimal(
    `${sign === "-" ? "-" : ""}${int || "0"}${frac ? "." + frac : ""}`,
    decimals,
  );
  const minDec = toDecimalString(min);
  if (minDec != null && compareDecimal(canonical, minDec) < 0) {
    return {
      value: null,
      error: `Minimum : ${formatDecimal(minDec, { decimals })}`,
    };
  }
  const maxDec = toDecimalString(max);
  if (maxDec != null && compareDecimal(canonical, maxDec) > 0) {
    return {
      value: null,
      error: `Maximum : ${formatDecimal(maxDec, { decimals })}`,
    };
  }
  return { value: canonical, error: null };
}
